import apiClient from './client';
import type {
  TrainingResource,
  CertificationEntry,
  EndorsementEntry,
  EndorsementCount,
  SkillDemandSnapshotEntry,
  TeamDashboardResult,
} from '../types';

// Training
export async function fetchTrainingBySkill(skillName: string): Promise<TrainingResource[]> {
  const { data } = await apiClient.get<TrainingResource[]>('/skill-development/training/by-skill', { params: { skillName } });
  return data;
}
export async function fetchTrainingForGaps(skills: string[]): Promise<Record<string, TrainingResource[]>> {
  const { data } = await apiClient.get<Record<string, TrainingResource[]>>('/skill-development/training/for-gaps', { params: { skills } });
  return data;
}
export async function createTraining(resource: Partial<TrainingResource>): Promise<TrainingResource> {
  const { data } = await apiClient.post<TrainingResource>('/skill-development/training', resource);
  return data;
}
export async function deleteTraining(id: string): Promise<void> {
  await apiClient.delete(`/skill-development/training/${id}`);
}
export async function fetchAllTraining(): Promise<TrainingResource[]> {
  const { data } = await apiClient.get<TrainingResource[]>('/skill-development/training');
  return data;
}

// Certifications
export async function fetchCertifications(employeeId: string): Promise<CertificationEntry[]> {
  const { data } = await apiClient.get<CertificationEntry[]>(`/skill-development/employees/${employeeId}/certifications`);
  return data;
}
export async function addCertification(employeeId: string, cert: { skillName: string; certName: string; issuingBody?: string; earnedDate?: string; expiryDate?: string; credentialUrl?: string }): Promise<CertificationEntry> {
  const { data } = await apiClient.post<CertificationEntry>(`/skill-development/employees/${employeeId}/certifications`, cert);
  return data;
}
export async function verifyCertification(certId: string): Promise<CertificationEntry> {
  const { data } = await apiClient.put<CertificationEntry>(`/skill-development/certifications/${certId}/verify`);
  return data;
}
export async function deleteCertification(certId: string): Promise<void> {
  await apiClient.delete(`/skill-development/certifications/${certId}`);
}
export async function fetchExpiringCertifications(withinDays: number = 90): Promise<CertificationEntry[]> {
  const { data } = await apiClient.get<CertificationEntry[]>('/skill-development/certifications/expiring', { params: { withinDays } });
  return data;
}

// Endorsements
export async function fetchEndorsements(employeeId: string): Promise<EndorsementEntry[]> {
  const { data } = await apiClient.get<EndorsementEntry[]>(`/skill-development/employees/${employeeId}/endorsements`);
  return data;
}
export async function fetchEndorsementCounts(employeeId: string): Promise<EndorsementCount[]> {
  const { data } = await apiClient.get<EndorsementCount[]>(`/skill-development/employees/${employeeId}/endorsement-counts`);
  return data;
}
export async function endorseSkill(employeeId: string, endorserId: string, skillName: string, comment?: string): Promise<EndorsementEntry> {
  const { data } = await apiClient.post<EndorsementEntry>(`/skill-development/employees/${employeeId}/endorsements`, { endorserId, skillName, comment });
  return data;
}

// Trends
export async function fetchSkillTrends(months: number = 12): Promise<SkillDemandSnapshotEntry[]> {
  const { data } = await apiClient.get<SkillDemandSnapshotEntry[]>('/skill-development/trends', { params: { months } });
  return data;
}
export async function generateTrendSnapshot(): Promise<void> {
  await apiClient.post('/skill-development/trends/generate');
}

// Team Dashboard
export async function fetchMyTeam(): Promise<TeamDashboardResult> {
  const { data } = await apiClient.get<TeamDashboardResult>('/skill-development/my-team');
  return data;
}

// Report Downloads
export function downloadReport(path: string): void {
  const baseUrl = apiClient.defaults.baseURL ?? '';
  window.open(`${baseUrl}/reports/${path}`, '_blank');
}
